export var RenderableUpdateTriggerState;
(function (RenderableUpdateTriggerState) {
    RenderableUpdateTriggerState[RenderableUpdateTriggerState["Created"] = 0] = "Created";
    RenderableUpdateTriggerState[RenderableUpdateTriggerState["Init"] = 1] = "Init";
    RenderableUpdateTriggerState[RenderableUpdateTriggerState["Updated"] = 2] = "Updated";
    RenderableUpdateTriggerState[RenderableUpdateTriggerState["Loaded"] = 3] = "Loaded";
    RenderableUpdateTriggerState[RenderableUpdateTriggerState["Changed"] = 4] = "Changed";
    RenderableUpdateTriggerState[RenderableUpdateTriggerState["Destroyed"] = 5] = "Destroyed";
})(RenderableUpdateTriggerState || (RenderableUpdateTriggerState = {}));
/**
 * Adds extra rendering next to whatever a model already looks like, without replacing it - for example a debug
 * gizmo or an attached audio source. (Despite the name, it does not "render" a facet directly; it usually just
 * creates/updates a plain three.js object and adds it to the scene via `this.owner.addObjectToScene(...)`.) A model
 * can have any number of `FacetRenderer`s active at the same time, one per registered facet type - see
 * {@link Renderable} for the "defines the model's own appearance" counterpart, which allows only one per model.
 *
 * Register a facet/`FacetRenderer` pair once in `client/src/FullAppConfig.ts`:
 * ```ts
 * RendererFacetFactory.register(PlaneFacet, PlaneFacetRenderer)
 * ```
 * Given a facet like:
 * ```ts
 * // PlaneFacet.ts
 * import { RenderableFacet, FacetFactory, type ByteStream, type Facet, type Number2 } from "flavr-common"
 *
 * export class PlaneFacet extends RenderableFacet {
 *   static readonly typestatic = "PlaneFacet"
 *
 *   get byteLength(): number {
 *     return Float32Array.BYTES_PER_ELEMENT * 2 // size
 *   }
 *
 *   constructor(public size: Number2 = [1, 1]) {
 *     super()
 *   }
 *
 *   serialization(stream: ByteStream): void {
 *     this.size = stream.number2(this.size)
 *   }
 *
 *   static is(facet: Facet): facet is PlaneFacet {
 *     return facet.type === PlaneFacet.typestatic
 *   }
 * }
 * FacetFactory.register(PlaneFacet)
 * ```
 * a matching `FacetRenderer` creates a mesh next to the model and keeps it in sync:
 * ```ts
 * // PlaneFacetRenderer.ts
 * import { FacetRenderer } from "@modules/rendering/renderable/Renderable"
 * import { Mesh, MeshBasicMaterial, PlaneGeometry } from "three"
 * import { PlaneFacet } from "./PlaneFacet"
 *
 * export class PlaneFacetRenderer extends FacetRenderer<PlaneFacet> {
 *   private mesh?: Mesh
 *
 *   destroy(): void {
 *     if (this.mesh === undefined) return
 *     this.owner.removeObjectFromScene(this.mesh)
 *     this.mesh.geometry.dispose()
 *     this.mesh = undefined
 *   }
 *
 *   setVisible(visible: boolean): void {
 *     if (this.mesh !== undefined) this.mesh.visible = visible
 *   }
 *
 *   updateFacet(facet: PlaneFacet): void {
 *     if (this.mesh === undefined || this.facet?.size !== facet.size) {
 *       if (this.mesh !== undefined) {
 *         this.owner.removeObjectFromScene(this.mesh)
 *         this.mesh.geometry.dispose()
 *       }
 *       this.mesh = new Mesh(new PlaneGeometry(facet.size[0], facet.size[1]), new MeshBasicMaterial())
 *       this.owner.addObjectToScene(this.mesh)
 *     }
 *   }
 *
 *   updateTransform(): void {
 *     if (this.mesh === undefined) return
 *     this.mesh.position.copy(this.owner.worldPosition)
 *     this.mesh.quaternion.copy(this.owner.worldOrientation)
 *     this.mesh.scale.copy(this.owner.worldScale)
 *   }
 * }
 * ```
 * `updateFacet(...)` is also called once right after this `FacetRenderer` is created (once the model has a valid
 * transform), which is why it doubles as the best place to create the mesh, and `destroy()` is called both when the
 * facet is removed from the model and when the model itself is destroyed.
 */
export class FacetRenderer {
    get facetType() {
        return this.constructor.facetType.typestatic;
    }
    get facetTypeConstructor() {
        return this.constructor.facetType;
    }
    constructor(owner) {
        this.owner = owner;
        this.updatedTime = 0;
    }
    registerAnimationCallback(callback) {
        this.owner.renderer.addAnimationCallback(this.owner.modelUID, this.facetTypeConstructor, callback);
    }
    unregisterAnimationCallback() {
        this.owner.renderer.removeAnimationCallback(this.owner.modelUID, this.facetTypeConstructor);
    }
    subscribeRoomChange() {
        if (this.onRoomChanged === undefined)
            return;
        if (this._onRoomChanged !== undefined)
            return;
        this._onRoomChanged = (type) => {
            if (this.onRoomChanged === undefined)
                return;
            this.onRoomChanged(type);
        };
        this.owner.onRoomChangedDelegate.add(this._onRoomChanged);
    }
    unsubscribeRoomChange() {
        if (this._onRoomChanged === undefined)
            return;
        this.owner.onRoomChangedDelegate.remove(this._onRoomChanged);
        this._onRoomChanged = undefined;
    }
    _destroy() {
        if (this.owner.triggerRenderableUpdate(this, RenderableUpdateTriggerState.Destroyed)) {
            if (this.owner.renderableChanged !== undefined) {
                this.owner.renderableChanged.delete(this.facetType);
                if (this.owner.renderableChanged.size === 0) {
                    this.owner.renderableChanged = undefined;
                }
            }
        }
    }
    clearSubscriptions() {
        this.unregisterAnimationCallback();
        this.unsubscribeRenderable();
        this.unsubscribeRoomChange();
    }
    destroyMe() {
        // Use the facet's own update time so server-sourced timestamps cannot block removal.
        this.owner.removeFacet(this.facetType, this.updatedTime + 1);
    }
    onInit(_facet) { }
    unsubscribeRenderable() {
        if (this.subscribedRenderableInfo === undefined)
            return;
        this.owner.unsubscribeRenderable(this.subscribedRenderableInfo.type, this.subscribedRenderableInfo.callback);
        this.subscribedRenderableInfo = undefined;
    }
    onOtherRenderableChanged(_state, _facetRenderer) { }
}
export function isRenderableType(type, renderable) {
    return renderable.type === type.typestatic;
}
/**
 * Defines the model's own visual appearance, for example its mesh or geometry - as opposed to a {@link FacetRenderer},
 * which only adds extra rendering next to it. Runs on the `RendererModule`. Because of that, a model can have only
 * one `Renderable` at a time, and unlike a `FacetRenderer` its facet must extend
 * {@link flavr-common!RenderableFacet | RenderableFacet} instead of the plain `Facet`.
 *
 * Register a `RenderableFacet`/`Renderable` pair once in `client/src/FullAppConfig.ts`:
 * ```ts
 * RenderableFactory.register(PlaneFacet, PlaneRenderable)
 * ```
 * Given the `PlaneFacet` from {@link FacetRenderer}'s example, a `Renderable` version looks almost identical - the
 * mesh it creates just *is* the model instead of sitting next to it:
 * ```ts
 * // PlaneRenderable.ts
 * import { Renderable, type RenderableType } from "@modules/rendering/renderable/Renderable"
 * import { Mesh, MeshBasicMaterial, PlaneGeometry } from "three"
 * import { PlaneFacet } from "./PlaneFacet"
 *
 * export class PlaneRenderable extends Renderable<PlaneFacet> {
 *   static readonly typestatic: RenderableType = "Primitive"
 *   readonly type = PlaneRenderable.typestatic
 *
 *   private mesh?: Mesh
 *
 *   destroy(): void {
 *     if (this.mesh === undefined) return
 *     this.owner.removeObjectFromScene(this.mesh)
 *     this.mesh.geometry.dispose()
 *     this.mesh = undefined
 *   }
 *
 *   setVisible(visible: boolean): void {
 *     if (this.mesh !== undefined) this.mesh.visible = visible
 *   }
 *
 *   updateFacet(facet: PlaneFacet): void {
 *     if (this.mesh === undefined || this.facet?.size !== facet.size) {
 *       if (this.mesh !== undefined) {
 *         this.owner.removeObjectFromScene(this.mesh)
 *         this.mesh.geometry.dispose()
 *       }
 *       this.mesh = new Mesh(new PlaneGeometry(facet.size[0], facet.size[1]), new MeshBasicMaterial())
 *       this.owner.addObjectToScene(this.mesh)
 *     }
 *   }
 *
 *   updateTransform(): void {
 *     if (this.mesh === undefined) return
 *     this.mesh.position.copy(this.owner.worldPosition)
 *     this.mesh.quaternion.copy(this.owner.worldOrientation)
 *     this.mesh.scale.copy(this.owner.worldScale)
 *   }
 * }
 * ```
 * Unlike `FacetRenderer.destroy()`, which is abstract, `Renderable.destroy()` already has a default implementation,
 * so overriding it (as above, to dispose the mesh) is only necessary when the `Renderable` allocated extra
 * resources.
 */
export class Renderable extends FacetRenderer {
    destroy() {
        if (this.owner.triggerRenderableUpdate(this, RenderableUpdateTriggerState.Destroyed)) {
            if (this.owner.renderableChanged) {
                this.owner.renderableChanged.delete(this.facetType);
                if (this.owner.renderableChanged.size === 0) {
                    this.owner.renderableChanged = undefined;
                }
            }
        }
    }
    getGeometry() {
        return undefined;
    }
    getCenter(center) {
        return center.copy(this.owner.worldPosition);
    }
}
